import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { PostureScanUserData } from '../types';
import { STORAGE_KEYS } from '../config';

interface UserDetailsFormProps {
  onSubmit: (data: PostureScanUserData) => void;
  initialData?: Partial<PostureScanUserData>;
  isLoading?: boolean;
}

const UserDetailsForm: React.FC<UserDetailsFormProps> = ({
  onSubmit,
  initialData,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<Partial<PostureScanUserData>>({
    userName: '',
    phoneNumber: '',
    emailId: '',
    ...initialData,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PostureScanUserData, string>>>({});
  const [consentChecked, setConsentChecked] = useState(false);

  // Load saved data from storage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (savedData && !initialData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
      } catch (error) {
        console.error('Failed to load saved user data:', error);
      }
    }
  }, [initialData]);

  const handleInputChange = (field: keyof PostureScanUserData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof PostureScanUserData, string>> = {};

    // Validate name
    if (!formData.userName?.trim()) {
      newErrors.userName = 'Name is required';
    }

    // Validate phone number
    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      // Remove all non-digit characters for validation
      const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 15) {
        newErrors.phoneNumber = 'Phone number must be between 10-15 digits';
      }
    }

    // Validate email
    if (!formData.emailId?.trim()) {
      newErrors.emailId = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      newErrors.emailId = 'Please enter a valid email address';
    }

    // Validate consent
    if (!consentChecked) {
      newErrors.userName = 'You must consent to continue'; // Using userName field to show general error
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const userData = formData as PostureScanUserData;
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      
      onSubmit(userData);
    }
  };

  return (
    <Card className="mb-4 border-0 shadow-lg">
      {/* Posture Image as Title */}
      <div>
        <img 
          src="/media/partner/posture.jpeg" 
          alt="AI Posture Scan" 
          style={{ width: '100%', height: 'auto', display: 'block', borderTopLeftRadius: '0.375rem', borderTopRightRadius: '0.375rem' }}
        />
      </div>
      
      <Card.Body className="p-5">
        {!consentChecked && (
          <Alert variant="info" className="mb-4">
            <Alert.Heading className="h6">Before we begin</Alert.Heading>
            <p className="mb-0 small">
              Please provide your details and consent to posture analysis. Your data will be processed securely.
            </p>
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Full Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.userName || ''}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  placeholder="Enter your full name"
                  disabled={isLoading}
                  isInvalid={!!errors.userName}
                  size="lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.userName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Phone Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.phoneNumber || ''}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="+1234567890"
                  disabled={isLoading}
                  isInvalid={!!errors.phoneNumber}
                  size="lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phoneNumber}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  10-15 digits, + optional
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Email Address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={formData.emailId || ''}
                  onChange={(e) => handleInputChange('emailId', e.target.value)}
                  placeholder="your.email@example.com"
                  disabled={isLoading}
                  isInvalid={!!errors.emailId}
                  size="lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emailId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="mb-4 mt-4">
            <Form.Group>
              <Form.Check
                type="checkbox"
                id="consent-checkbox"
                label={
                  <span>
                    I consent to posture scan processing and understand that my images will be analyzed by AI. <span className="text-danger">*</span>
                  </span>
                }
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                disabled={isLoading}
                className="fs-6"
              />
            </Form.Group>
          </div>

          <div className="d-grid gap-2">
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading || !consentChecked}
              size="lg"
              className="py-3"
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                'Continue to Image Upload'
              )}
            </Button>
          </div>

          <div className="mt-4 text-center">
            <small className="text-muted">
              Your privacy is important to us. All data is processed securely.
            </small>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default UserDetailsForm;
